import { useLocale, useT, type Translate } from "../../shared/i18n";
import type { ExportItem } from "../../shared/api/client";
import { Banner, Button, ListRow } from "../../shared/ui";
import type { ExportNotice } from "./useExports";
import { fill, formatDateTime } from "./accountFormat";

/** Banner text for the outcome of an export or link action. */
export function ExportNoticeBanner({ notice }: { notice: ExportNotice }) {
  const t = useT();
  const { locale } = useLocale();
  if (notice.kind === "requested")
    return <Banner tone="success">{t("account.export.requested")}</Banner>;
  if (notice.kind === "in_progress")
    return <Banner tone="info">{t("account.export.inProgress")}</Banner>;
  if (notice.kind === "cooldown")
    return (
      <Banner tone="warning">
        {notice.retryAfter
          ? fill(t("account.export.cooldown"), {
              when: formatDateTime(locale, notice.retryAfter),
            })
          : t("account.export.cooldownSoon")}
      </Banner>
    );
  if (notice.kind === "no_telegram")
    return <Banner tone="warning">{t("account.export.noTelegram")}</Banner>;
  if (notice.kind === "links_failed")
    return <Banner tone="error">{t("account.links.failed")}</Banner>;
  return <Banner tone="error">{t("account.export.failed")}</Banner>;
}

function statusLabel(t: Translate, status: string): string {
  if (status === "queued") return t("account.links.status.queued");
  if (status === "building") return t("account.links.status.building");
  if (status === "ready") return t("account.links.status.ready");
  return t("account.links.status.other");
}

/** Active links with per-item and bulk revoke. Never shows a token. */
export function ExportLinks({
  items,
  busy,
  onRevoke,
  onRevokeAll,
}: {
  items: readonly ExportItem[];
  busy: boolean;
  onRevoke: (id: string) => void;
  onRevokeAll: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  if (items.length === 0)
    return <p className="text-body text-fg-muted">{t("account.links.none")}</p>;
  return (
    <div className="flex flex-col gap-2">
      <ul className="[&>li:last-child>[data-row]]:after:hidden">
        {items.map((item, index) => (
          <li key={item.id}>
            <ListRow
              kind="setting"
              title={statusLabel(t, item.status)}
              subtitle={[
                item.expires_at
                  ? fill(t("account.links.expires"), {
                      when: formatDateTime(locale, item.expires_at),
                    })
                  : null,
                fill(t("account.links.downloads"), {
                  count: item.downloads_left,
                }),
              ]
                .filter(Boolean)
                .join(" · ")}
              trailing={
                <Button
                  size="sm"
                  variant="dangerSoft"
                  disabled={busy}
                  aria-label={fill(t("account.links.revokeNumbered"), {
                    n: index + 1,
                  })}
                  onClick={() => {
                    onRevoke(item.id);
                  }}
                >
                  {t("account.links.revoke")}
                </Button>
              }
            />
          </li>
        ))}
      </ul>
      <Button
        variant="dangerSoft"
        disabled={busy}
        onClick={onRevokeAll}
        className="self-start"
      >
        {t("account.links.revokeAll")}
      </Button>
    </div>
  );
}
